import { Avatar, Group, Stack, Text, Tile } from "@assetwatch/ui";
import { Link } from "@tanstack/react-router";
import type { NavigationOriginSearch } from "../lib/navigationOrigin";
import type { AssetId } from "../lib/workIds";
import type { WorkSearch } from "../lib/workSearch";
import type { WorkRequestApproval } from "../types/workRequestApi";

export type ApprovalAssetLink = {
  assetId: AssetId;
  search: WorkSearch & NavigationOriginSearch;
};

export function ApproveWorkRequestSummary({
  approval,
  assetLink,
}: {
  approval: WorkRequestApproval;
  assetLink: ApprovalAssetLink | null;
}) {
  const requester = approval.submittedBy ?? "Requester";
  const initials =
    requester
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "U";
  return (
    <Tile tone="base" padding="comfortable" width="full">
      <Stack gap={12}>
        <Group align="start" justify="between" wrap="wrap">
          <Group align="center" gap={12}>
            <Avatar
              aria-label={requester}
              initials={initials}
              size="md"
              variant="external"
            />
            <Stack gap={4}>
              <Group gap={8} wrap="wrap">
                <Text as="span" size="sm">
                  {requester}
                </Text>
                <Text as="span" size="sm" tone="subtle">
                  - Operator
                </Text>
                <Text as="span" size="sm" tone="subtle">
                  - {approval.asset.locationName ?? ""}
                </Text>
              </Group>
              {approval.asset.id > 0 && assetLink ? (
                <Link
                  aria-label={`Open Asset Detail for ${approval.asset.name}`}
                  className="rounded-(--radius-sm) outline-none focus-visible:ring-2 focus-visible:ring-button-focus-ring"
                  params={{ assetId: assetLink.assetId }}
                  search={assetLink.search}
                  to="/assets/$assetId"
                >
                  <Text size="sm" tone="subtle">
                    {approval.asset.code ? `${approval.asset.code}  -  ` : ""}
                    {approval.asset.name}
                  </Text>
                </Link>
              ) : (
                <Text size="sm" tone="subtle">
                  {approval.asset.name}
                </Text>
              )}
            </Stack>
          </Group>
          <Text size="sm" tone="subtle">
            Submitted{" "}
            {new Intl.DateTimeFormat(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            }).format(new Date(approval.submittedAt))}
          </Text>
        </Group>
        <Tile tone="muted" padding="compact" width="full">
          <Text>{approval.description}</Text>
        </Tile>
        <Text size="sm" tone="subtle">
          Status: {approval.status}
        </Text>
      </Stack>
    </Tile>
  );
}
